import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, StyleSheet, Alert, BackHandler, Platform } from "react-native";
import { useRoute, useNavigation } from "@react-navigation/native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigator";
import GameTable from "../Components/Game";
import InvitePlayerToTableModal from "../Components/Modals/InvitePlayerToTableModal";
import BecomeVipModal from "../Components/Modals/BecomeVipModal";
// Metro bundler will automatically resolve platform-specific files:
// - TutorialTable.web.tsx for web
// - TutorialTable.android.tsx for Android  
// - TutorialTable.native.tsx for iOS
import TutorialTable from "../Components/Game/TutorialTable";

type GameScreenProps = NativeStackScreenProps<RootStackParamList, "Tutorial">;

const TutorialScreen: React.FC = () => {
  const route = useRoute<GameScreenProps["route"]>();
  const navigation = useNavigation<GameScreenProps["navigation"]>();

  const {
    tableId,
    roomSlug,
    isTutorial,
    tableName,
    tableStringId,
    isTournamentRoom,
  } = route.params || {};
  const [showInvitesModal, setShowInvitesModal] = useState(false);
  const [showBecomeVipModal, setShowBecomeVipModal] = useState(false);
  const stableTimestamp = useRef(
    (route.params && route.params.timestamp) || Date.now()
  ).current;
  // Handle Android back button
  useEffect(() => {
    if (Platform.OS === "android") {
      const backHandler = BackHandler.addEventListener(
        "hardwareBackPress",
        () => {
          Alert.alert(
            "Exit Tutorial",
            "Are you sure you want to exit the tutorial?",
            [
              {
                text: "Cancel",
                style: "cancel",
              },
              {
                text: "Exit",
                style: "destructive",
                onPress: () => {
                  navigation.goBack();
                },
              },
            ]
          );
          return true; // Prevent default back behavior
        }
      );

      return () => backHandler.remove();
    }
  }, [navigation]);

  const handleGameEvent = useCallback(
    (event: string, data?: any) => {
      console.log("Game event received:", event, data);

      switch (event) {
        case "goToLobby":
          navigation.replace("Lobby");
          break;

        case "becomeVIP":
          setShowBecomeVipModal(true);
          break;

        case "notEnoughChips":
          // Handle not enough chips modal
          Alert.alert(
            "Not Enough Chips",
            "You need more chips to continue playing."
          );
          break;

        case "buyChips":
          // Handle buy chips modal
          Alert.alert("Buy Chips", "Redirecting to shop...");
          break;

        case "invite":
          // Handle invite modal
          setShowInvitesModal(true);
          break;

        case "setSound":
          // Handle sound settings
          console.log("Sound setting changed:", data);
          break;

        case "openNotifications":
          // Handle notifications
          navigation.navigate("Messages");
          break;

        case "showBlackscreenAlert":
          // Handle blackscreen warning
          Alert.alert(
            "Performance Warning",
            "Your device may have performance issues."
          );
          break;

        case "hideBlackscreenAlert":
          // Hide blackscreen warning
          console.log("Blackscreen alert hidden");
          break;

        case "tableDoesNotExist":
          // Handle table not found
          Alert.alert("Table Not Found", "This table no longer exists.", [
            { text: "OK", onPress: () => navigation.goBack() },
          ]);
          break;

        default:
          console.log("Unhandled game event:", event, data);
      }
    },
    [navigation]
  );

  return (
    <View style={styles.container}>
      <TutorialTable
        key={`${tableId}-${stableTimestamp}`}
        tableId={tableId}
        roomSlug={roomSlug}
        isTutorial={isTutorial}
        onGameEvent={handleGameEvent}
        isTournamentRoom={isTournamentRoom}
      />
      {showBecomeVipModal && (
        <BecomeVipModal onClose={() => setShowBecomeVipModal(false)} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#000",
  },
});

export default TutorialScreen;
